import { useState } from 'react';
import { DAY_CODES, checkedInToday, computeStreak, lastWeekCount, thisWeekCount } from '../utils';

const TYPE_OPTIONS = [
  { value: 'positive', label: 'Build', desc: 'Daily compliance — keep doing it' },
  { value: 'negative', label: 'Break', desc: 'Want zero — clean streak' },
  { value: 'reduce', label: 'Reduce', desc: 'Trend down — weekly target' }
];

const FREQ_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'days', label: 'Specific days' },
  { value: 'weekly', label: 'X / week' }
];

const DAY_NAMES = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday'
};

export default function HabitsTab({ habits, saveHabit, deleteHabit, checkInHabit, toggleHabitPause }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="habits-tab">
      <div className="tab-head">
        <h3>Habits</h3>
        <button className="link-btn" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
          {adding ? 'Cancel' : '+ New habit'}
        </button>
      </div>
      {adding && (
        <HabitForm
          onSave={async (h) => {
            await saveHabit(h);
            setAdding(false);
          }}
        />
      )}
      {habits.length === 0 && !adding && (
        <p className="muted">No habits yet. Pick one to start with.</p>
      )}
      <ul className="habits-list">
        {habits.map((h) => (
          <HabitCard
            key={h.id}
            habit={h}
            onCheckIn={(note) => checkInHabit(h, note)}
            onTogglePause={() => toggleHabitPause(h)}
            onDelete={() => deleteHabit(h.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function HabitForm({ onSave }) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('positive');
  const [freqType, setFreqType] = useState('daily');
  const [days, setDays] = useState(['MO', 'WE', 'FR']);
  const [perWeek, setPerWeek] = useState(3);
  const [weeklyTarget, setWeeklyTarget] = useState(3);

  function toggleDay(d) {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    const frequency =
      freqType === 'days'
        ? { type: 'days', days }
        : freqType === 'weekly'
          ? { type: 'weekly', count: Number(perWeek) || 1 }
          : { type: 'daily' };
    const habit = { title, type, frequency };
    if (type === 'reduce') habit.weeklyTarget = Number(weeklyTarget) || 1;
    await onSave(habit);
    setTitle('');
  }

  return (
    <form className="habit-form" onSubmit={submit}>
      <input
        autoFocus
        placeholder="Habit"
        aria-label="Habit name"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <fieldset className="habit-form__types">
        <legend className="sr-only">Habit type</legend>
        {TYPE_OPTIONS.map((opt) => (
          <label key={opt.value} className={`habit-form__type${type === opt.value ? ' habit-form__type--active' : ''}`}>
            <input
              type="radio"
              name="habit-type"
              value={opt.value}
              checked={type === opt.value}
              onChange={() => setType(opt.value)}
            />
            <strong>{opt.label}</strong>
            <span>{opt.desc}</span>
          </label>
        ))}
      </fieldset>
      {type !== 'reduce' && (
        <fieldset className="habit-form__freq">
          <legend>Frequency</legend>
          <div className="habit-form__freq-options">
            {FREQ_OPTIONS.map((opt) => (
              <label key={opt.value} className={`pill${freqType === opt.value ? ' pill--active' : ''}`}>
                <input
                  type="radio"
                  name="habit-freq"
                  value={opt.value}
                  checked={freqType === opt.value}
                  onChange={() => setFreqType(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {freqType === 'days' && (
            <div className="habit-form__days" role="group" aria-label="Days of the week">
              {DAY_CODES.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`pill${days.includes(d) ? ' pill--active' : ''}`}
                  onClick={() => toggleDay(d)}
                  aria-pressed={days.includes(d)}
                  aria-label={DAY_NAMES[d]}
                >
                  {d.toLowerCase().slice(0, 2).replace(/^./, (c) => c.toUpperCase())}
                </button>
              ))}
            </div>
          )}
          {freqType === 'weekly' && (
            <label className="habit-form__count">
              <input
                type="number"
                min={1}
                max={7}
                value={perWeek}
                onChange={(e) => setPerWeek(e.target.value)}
              />
              times per week
            </label>
          )}
        </fieldset>
      )}
      {type === 'reduce' && (
        <label className="habit-form__count">
          Max
          <input
            type="number"
            min={1}
            value={weeklyTarget}
            onChange={(e) => setWeeklyTarget(e.target.value)}
          />
          times per week
        </label>
      )}
      <button type="submit" className="primary-btn">
        Save habit
      </button>
    </form>
  );
}

function HabitCard({ habit, onCheckIn, onTogglePause, onDelete }) {
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const checked = checkedInToday(habit);

  const typeLabel = habit.type === 'negative' ? 'Break' : habit.type === 'reduce' ? 'Reduce' : 'Build';
  const freq = habit.frequency || { type: 'daily' };
  const freqLabel =
    freq.type === 'days'
      ? (freq.days || []).join(' ')
      : freq.type === 'weekly'
        ? `${freq.count}× / week`
        : 'Daily';

  let metric;
  if (habit.type === 'reduce') {
    const tw = thisWeekCount(habit.checkIns);
    const lw = lastWeekCount(habit.checkIns);
    const target = habit.weeklyTarget ?? 0;
    const arrow = tw < lw ? '↓' : tw > lw ? '↑' : '→';
    metric = (
      <span className="habit-card__metric">
        {tw} / {target} this week <span className="habit-card__trend">{arrow} {lw} last</span>
      </span>
    );
  } else if (habit.type === 'negative') {
    const streak = computeStreak(habit);
    metric = (
      <span className="habit-card__metric">
        {streak === null ? 'no slips logged' : `${streak}d clean`}
      </span>
    );
  } else {
    const streak = computeStreak(habit);
    metric = (
      <span className="habit-card__metric">
        <span className="streak">🔥 {streak}</span>
      </span>
    );
  }

  function commitCheck() {
    onCheckIn(note);
    setShowNote(false);
    setNote('');
  }

  return (
    <li className={`habit-card habit-card--${habit.type}${habit.paused ? ' habit-card--paused' : ''}`}>
      <button
        className={`habit-card__check${checked ? ' habit-card__check--on' : ''}`}
        onClick={() => (habit.type === 'positive' && !checked ? setShowNote(true) : onCheckIn())}
        disabled={habit.paused}
        aria-label={
          habit.type === 'negative'
            ? `Log a slip for "${habit.title}"`
            : habit.type === 'reduce'
              ? `Log an occurrence of "${habit.title}"`
              : `Check in "${habit.title}"`
        }
        aria-pressed={checked}
      >
        <span aria-hidden="true">{checked ? '✓' : habit.type === 'negative' ? '!' : '○'}</span>
      </button>
      <div className="habit-card__body">
        <div className="habit-card__top">
          <h4>{habit.title}</h4>
          <span className="habit-card__type">{typeLabel}</span>
          <span className="habit-card__freq">{freqLabel}</span>
        </div>
        {metric}
      </div>
      <div className="habit-card__actions">
        <button
          className="link-btn"
          onClick={onTogglePause}
          aria-label={habit.paused ? `Resume habit "${habit.title}"` : `Pause habit "${habit.title}"`}
        >
          <span aria-hidden="true">{habit.paused ? '▶' : '⏸'}</span>
        </button>
        <button
          className="link-btn"
          onClick={() => setConfirmingDelete(true)}
          aria-label={`Delete habit "${habit.title}"`}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {showNote && (
        <div className="habit-card__note-row">
          <input
            autoFocus
            placeholder="how did it go? (optional)"
            aria-label="How did it go? Optional note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && commitCheck()}
          />
          <button className="link-btn" onClick={commitCheck}>
            Save
          </button>
          <button className="link-btn" onClick={() => { setShowNote(false); onCheckIn(); }}>
            Skip note
          </button>
        </div>
      )}
      {confirmingDelete && (
        <DeleteConfirm
          habit={habit}
          onPause={() => {
            setConfirmingDelete(false);
            if (!habit.paused) onTogglePause();
          }}
          onDelete={() => {
            setConfirmingDelete(false);
            onDelete();
          }}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </li>
  );
}

function DeleteConfirm({ habit, onPause, onDelete, onCancel }) {
  const [armed, setArmed] = useState(false);
  const checkInCount = (habit.checkIns || []).length;
  const streak = computeStreak(habit);

  let summary;
  if (habit.type === 'reduce') {
    summary =
      checkInCount > 0
        ? `${checkInCount} logged occurrence${checkInCount === 1 ? '' : 's'} will be erased.`
        : 'Nothing logged yet.';
  } else if (habit.type === 'negative') {
    summary =
      streak === null || streak === 0
        ? `${checkInCount} slip${checkInCount === 1 ? '' : 's'} logged.`
        : `${streak}-day clean streak ends here.`;
  } else {
    summary =
      streak > 0
        ? `${streak}-day streak ends here.`
        : `${checkInCount} check-in${checkInCount === 1 ? '' : 's'} will be erased.`;
  }

  return (
    <div className="habit-card__confirm" role="alertdialog" aria-label={`Delete habit "${habit.title}"?`}>
      <div className="habit-card__confirm-text">
        <strong>Delete "{habit.title}"?</strong>
        <span>{summary}</span>
      </div>
      <div className="habit-card__confirm-actions">
        {!habit.paused && (
          <button className="link-btn" onClick={onPause}>
            Pause instead
          </button>
        )}
        <button className="link-btn" onClick={onCancel}>
          Keep it
        </button>
        {!armed ? (
          <button
            className="habit-card__confirm-arm"
            onClick={() => setArmed(true)}
          >
            Delete
          </button>
        ) : (
          <button
            className="habit-card__confirm-final"
            onClick={onDelete}
          >
            Confirm delete
          </button>
        )}
      </div>
    </div>
  );
}
